import {
  getLibraryTypeByLibrary,
  getManageCodeByLibrary,
  Library_code,
  normalizeLibraryCode,
} from "../types/Library";
import { NameMemberRecommendRequestPayload, RecommendBooksResponse } from "../types/User";

const MISSING_QUERY_ERROR = "사용자 정보가 없습니다. 접속 URL을 확인해주세요.";
const INVALID_QUERY_ERROR = "사용자 정보 형식이 올바르지 않습니다. 접속 URL을 확인해주세요.";
const DIRECT_RECOMMEND_PATH = "personal-recommendation";
const USER_NAME_MAX_LENGTH = 50;
const USER_NO_MAX_LENGTH = 32;
const USER_NO_PATTERN = /^[0-9]+$/;

type LocationSnapshot = Pick<Location, "pathname" | "search" | "hash">;

export type CapturedDirectRecommendAttempt =
  | {
      kind: "invalid";
      sourcePathname: string;
      normalizedLibrary: Library_code;
      errorMessage: string;
      status: "ready" | "complete";
    }
  | {
      kind: "valid";
      sourcePathname: string;
      normalizedLibrary: Library_code;
      payload?: NameMemberRecommendRequestPayload;
      status: "ready" | "in-flight" | "retryable" | "complete";
      request?: Promise<unknown>;
      abortRequest?: () => void;
    };

export type DirectRecommendCaptureResult = {
  attempt: CapturedDirectRecommendAttempt | null;
  sanitizedRelativeUrl: string | null;
};

let initialAttempt: CapturedDirectRecommendAttempt | null = null;
let initialCaptureCompleted = false;

const getLibraryFromDirectRecommendPath = (pathname: string) => {
  const pathParts = pathname.split("/").filter(Boolean);
  if (pathParts.length !== 2 || pathParts[1] !== DIRECT_RECOMMEND_PATH) {
    return null;
  }

  try {
    return decodeURIComponent(pathParts[0]);
  } catch {
    return null;
  }
};

const getSingleTrimmedParam = (searchParams: URLSearchParams, key: string) => {
  const values = searchParams.getAll(key);
  if (values.length !== 1) return null;
  return values[0].trim();
};

const hasControlCharacter = (value: string) => {
  return value.split("").some((character) => {
    const characterCode = character.charCodeAt(0);
    return characterCode <= 31 || characterCode === 127;
  });
};

const buildSanitizedRelativeUrl = (location: LocationSnapshot, searchParams: URLSearchParams) => {
  searchParams.delete("user_name");
  searchParams.delete("user_no");

  const remainingSearch = searchParams.toString();
  return `${location.pathname}${remainingSearch ? `?${remainingSearch}` : ""}${location.hash}`;
};

export const inspectDirectRecommendQuery = (
  location: LocationSnapshot
): DirectRecommendCaptureResult => {
  const searchParams = new URLSearchParams(location.search);
  const hasDirectRecommendParam =
    searchParams.has("user_name") || searchParams.has("user_no");

  if (!hasDirectRecommendParam) {
    return { attempt: null, sanitizedRelativeUrl: null };
  }

  const userName = getSingleTrimmedParam(searchParams, "user_name");
  const userNo = getSingleTrimmedParam(searchParams, "user_no");
  const sanitizedRelativeUrl = buildSanitizedRelativeUrl(location, searchParams);
  const library = getLibraryFromDirectRecommendPath(location.pathname);
  const normalizedLibrary = normalizeLibraryCode(library);
  const manageCode = getManageCodeByLibrary(library);

  if (
    !normalizedLibrary ||
    !manageCode ||
    getLibraryTypeByLibrary(normalizedLibrary) !== "asan"
  ) {
    return { attempt: null, sanitizedRelativeUrl };
  }

  if (userName === null || userNo === null || !userName || !userNo) {
    return {
      attempt: {
        kind: "invalid",
        sourcePathname: location.pathname,
        normalizedLibrary,
        errorMessage: MISSING_QUERY_ERROR,
        status: "ready",
      },
      sanitizedRelativeUrl,
    };
  }

  const hasInvalidFormat =
    userName.length > USER_NAME_MAX_LENGTH ||
    hasControlCharacter(userName) ||
    userNo.length > USER_NO_MAX_LENGTH ||
    !USER_NO_PATTERN.test(userNo);

  if (hasInvalidFormat) {
    return {
      attempt: {
        kind: "invalid",
        sourcePathname: location.pathname,
        normalizedLibrary,
        errorMessage: INVALID_QUERY_ERROR,
        status: "ready",
      },
      sanitizedRelativeUrl,
    };
  }

  return {
    attempt: {
      kind: "valid",
      sourcePathname: location.pathname,
      normalizedLibrary,
      payload: {
        user_name: userName,
        user_no: userNo,
        manage_code: manageCode,
      },
      status: "ready",
    },
    sanitizedRelativeUrl,
  };
};

export const isRetryableDirectRecommendResponse = (response: RecommendBooksResponse) => {
  if (response.response_body) return false;
  return ![0, 3406, 9404].includes(response.status_code);
};

export const captureInitialDirectRecommendQuery = () => {
  if (initialCaptureCompleted) return;
  initialCaptureCompleted = true;

  const locationSnapshot: LocationSnapshot = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
  const searchParams = new URLSearchParams(locationSnapshot.search);
  const hasSensitiveQuery = searchParams.has("user_name") || searchParams.has("user_no");

  if (hasSensitiveQuery) {
    const sanitizedRelativeUrl = buildSanitizedRelativeUrl(locationSnapshot, searchParams);
    const currentRelativeUrl = `${locationSnapshot.pathname}${locationSnapshot.search}${locationSnapshot.hash}`;

    if (sanitizedRelativeUrl !== currentRelativeUrl) {
      window.history.replaceState(window.history.state, document.title, sanitizedRelativeUrl);
    }
  }

  initialAttempt = inspectDirectRecommendQuery(locationSnapshot).attempt;
};

export const getInitialDirectRecommendAttempt = (
  library: string | null | undefined,
  pathname: string
) => {
  if (!initialAttempt || initialAttempt.sourcePathname !== pathname) return null;
  if (initialAttempt.normalizedLibrary !== normalizeLibraryCode(library)) return null;
  if (initialAttempt.status === "complete") return null;
  return initialAttempt;
};

export const completeInitialDirectRecommendAttempt = (
  attempt: CapturedDirectRecommendAttempt
) => {
  if (attempt.kind === "valid") {
    attempt.payload = undefined;
    attempt.request = undefined;
    attempt.abortRequest = undefined;
  }
  attempt.status = "complete";
};

export const discardInitialDirectRecommendAttempt = () => {
  if (!initialAttempt) return;
  if (initialAttempt.kind === "valid") initialAttempt.abortRequest?.();
  completeInitialDirectRecommendAttempt(initialAttempt);
  initialAttempt = null;
};

export const startInitialDirectRecommendRequest = (
  attempt: CapturedDirectRecommendAttempt,
  start: (payload: NameMemberRecommendRequestPayload) => Promise<unknown>
) => {
  if (attempt.kind !== "valid") return Promise.resolve();
  if (attempt.request) return attempt.request;
  if (attempt.status === "complete" || !attempt.payload) return Promise.resolve();

  const payload = attempt.payload;
  attempt.payload = undefined;
  attempt.status = "in-flight";

  const request = Promise.resolve()
    .then(() => start(payload))
    .then(
      (result) => {
        completeInitialDirectRecommendAttempt(attempt);
        return result;
      },
      (error) => {
        if (attempt.status !== "complete") {
          attempt.payload = payload;
          attempt.status = "retryable";
        }
        throw error;
      }
    )
    .finally(() => {
      attempt.request = undefined;
    });

  attempt.request = request;
  return request;
};
